
import React, { useState } from 'react';
import { useAuth } from '@/contexts/auth';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import PollForm from './PollForm';
import PollCarousel from './PollCarousel';
import EmptyPollState from './EmptyPollState';
import CreatePollButton from './CreatePollButton';
import { usePollData } from './hooks/usePollData';

interface PollListProps {
  tripId: string;
  showClosed: boolean;
}

const PollList: React.FC<PollListProps> = ({ tripId, showClosed }) => {
  const { user } = useAuth();
  const [showPollForm, setShowPollForm] = useState(false);
  const { polls, isLoading, error, refetch, markPollAsDeleted } = usePollData(tripId, showClosed);

  const handlePollCreated = () => {
    setShowPollForm(false);
    refetch();
    toast.success('Poll created successfully!');
  };
  
  const handlePollDeleted = (pollId: string) => {
    // Mark the poll as deleted locally to prevent it from reappearing
    markPollAsDeleted(pollId);
  };

  if (isLoading) {
    return <div className="py-4 text-center">Loading polls...</div>;
  }

  if (error) {
    return <div className="py-4 text-center text-red-500">Error loading polls</div>;
  }

  return (
    <div className="space-y-6">
      {user && (
        <div className="mb-4">
          {!showPollForm ? (
            <CreatePollButton onClick={() => setShowPollForm(true)} />
          ) : (
            <Card>
              <CardContent className="pt-6">
                <PollForm 
                  tripId={tripId} 
                  onSuccess={handlePollCreated}
                  onCancel={() => setShowPollForm(false)}
                />
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {polls && polls.length > 0 ? (
        <PollCarousel polls={polls} onPollDeleted={handlePollDeleted} />
      ) : (
        <EmptyPollState 
          showClosed={showClosed}
          onCreatePoll={() => setShowPollForm(true)}
          isAuthenticated={!!user}
        />
      )}
    </div>
  );
};

export default PollList;
